// Lógica pura de la pantalla de Alertas del cliente: qué filas muestra cada filtro y los números de
// arriba. Sin React, para poder testearla sin montar nada.
import {itemSeverityLevel} from '../../activity_log/detail_renderers/alert_indicators';

export const FILTER_ALL = 'todas';
export const FILTER_CRITIC = 'criticas';
export const FILTER_TODAY = 'hoy';
export const FILTER_REPEATED = 'se-repiten';
export const FILTER_SNOOZED = 'silenciadas';

export const ALERT_FILTERS = [FILTER_ALL, FILTER_CRITIC, FILTER_TODAY, FILTER_REPEATED, FILTER_SNOOZED];

const LEVEL_CRITIC = 'CRITIC';

function isSameLocalDay(isoDate, today) {
    if (!isoDate) return false;
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return false;
    return date.getFullYear() === today.getFullYear()
        && date.getMonth() === today.getMonth()
        && date.getDate() === today.getDate();
}

const PREDICATES = {
    // La MISMA gravedad que pinta la fila (`severityOf`): el ODS cuenta aunque su `level` sea la comparación.
    // El backend cuenta igual cuando recorta la lista (`is_critic_item`, `chip_counts`).
    [FILTER_CRITIC]: (item) => itemSeverityLevel(item) === LEVEL_CRITIC,
    [FILTER_TODAY]: (item, today) => isSameLocalDay(item.first_seen, today),
    // `occurrences` son DISPAROS (la fila se escribe cuando la alerta pasa a crítica): la primera
    // también cuenta, así que «se repite» es a partir de dos.
    [FILTER_REPEATED]: (item) => Number(item.occurrences || 0) > 1,
};

/**
 * `{active, snoozed}` de lo que el backend manda (`GET agency-client/<id>/active-alerts/`).
 *
 * Las vigentes son las NO silenciadas: es la lista que cuenta el tile del Panel y la pill del
 * listado, y tiene que dar el mismo número (criterio de aceptación del mockup de alertas).
 */
export function splitAlerts(payload) {
    const items = (payload && payload.items) || [];
    return {
        active: items.filter((item) => !item.snoozed),
        snoozed: items.filter((item) => item.snoozed),
    };
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

// Una silenciada vence «esta semana» si su `snoozed_until` cae en los próximos 7 días. Sin fecha =
// silenciada para siempre: no vence nunca.
function expiresWithinAWeek(item, today) {
    if (!item.snoozed_until) return false;
    const until = new Date(item.snoozed_until).getTime();
    if (isNaN(until)) return false;
    return until >= today.getTime() && until - today.getTime() <= WEEK_MS;
}

/** Los números de arriba, siempre sobre TODO (los filtros no los mueven). */
export function alertsSummary(payload, today = new Date()) {
    const {active, snoozed} = splitAlerts(payload);
    return {
        active: active.length,
        critic: active.filter((item) => PREDICATES[FILTER_CRITIC](item)).length,
        today: active.filter((item) => PREDICATES[FILTER_TODAY](item, today)).length,
        repeated: active.filter((item) => PREDICATES[FILTER_REPEATED](item)).length,
        snoozed: snoozed.length,
        // Las que vuelven a sonar pronto: lo que el mockup pone debajo de «Silenciadas».
        snoozedThisWeek: snoozed.filter((item) => expiresWithinAWeek(item, today)).length,
    };
}

/**
 * Las filas de un filtro, `{active, snoozed}`.
 *
 * «Todas» trae las vigentes y, al final, las silenciadas (como el popover del listado: una alerta
 * silenciada sigue pasando, y es donde se reactiva). «Silenciadas» trae sólo esas. Los demás
 * filtros miran sólo las vigentes: una crítica silenciada no es trabajo de hoy.
 */
export function alertsForFilter(payload, filter, today = new Date()) {
    const {active, snoozed} = splitAlerts(payload);
    if (filter === FILTER_SNOOZED) return {active: [], snoozed};
    const predicate = PREDICATES[filter];
    if (!predicate) return {active, snoozed};
    return {active: active.filter((item) => predicate(item, today)), snoozed: []};
}

export function filterFromParam(value) {
    return ALERT_FILTERS.includes(value) ? value : FILTER_ALL;
}
